/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/design-system/providers/ThemeProvider.tsx
 * layer: web
 * area: providers
 * kind: source
 * web_root: providers
 * relpath: web/src/platform/design-system/providers/ThemeProvider.tsx
 * filename: ThemeProvider.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import { createContext, useEffect, useState, useCallback, PropsWithChildren } from 'react';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'hyperion-theme';

function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  // Guard against environments where localStorage exists as a property
  // but its methods are missing/stubbed (some test renderers, sandboxed
  // iframes, certain happy-dom configurations).
  try {
    const stored = window.localStorage?.getItem?.(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // SecurityError from sandboxed iframes — fall through to default.
  }
  // Default to light — dark mode is not yet polished
  return 'light';
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: PropsWithChildren) {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    const initial = getStoredTheme();
    return initial === 'system' ? getSystemTheme() : initial;
  });

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    const updateTheme = () => {
      let resolved: ResolvedTheme;

      if (theme === 'system') {
        resolved = getSystemTheme();
      } else {
        resolved = theme;
      }

      setResolvedTheme(resolved);

      // Add transition class for smooth theme change
      root.classList.add('theme-transition');

      if (resolved === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }

      // Remove transition class after animation
      const timeout = setTimeout(() => {
        root.classList.remove('theme-transition');
      }, 200);

      return () => clearTimeout(timeout);
    };

    updateTheme();

    // Listen for system theme changes when in system mode
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => updateTheme();
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
  }, [theme]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    try {
      window.localStorage?.setItem?.(STORAGE_KEY, newTheme);
    } catch {
      // ignore — sandboxed contexts / quota errors / stubbed test envs
    }
  }, []);

  const toggleTheme = useCallback(() => {
    const newTheme = resolvedTheme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  }, [resolvedTheme, setTheme]);

  return (
    <ThemeContext value={{ theme, resolvedTheme, setTheme, toggleTheme }}>{children}</ThemeContext>
  );
}
